import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { SUBJECT_LABEL } from '@platform/schema/content';
import { GOALS_MAX } from '@platform/schema/onboarding';
import { device } from '@platform/tokens';

import { TOPICS_READY, sampleOf, type TopicsReady } from './config';

import type { Control } from './controls';
import type { FlowStep, OnboardingGoal } from '@platform/schema/onboarding';
import type { PreviewSample } from '@platform/schema/viewer';
import type { ReactNode } from 'react';

type BuiltIn = Extract<FlowStep, { kind: 'BUILT_IN' }>;

const LIMITS = [1, 2, 3] as const;

function Rules({ items }: { items: readonly ReactNode[] }) {
  return (
    <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 3 }}>
      {items.map((item, at) => (
        <Typography component="li" variant="body2" key={at}>
          {item}
        </Typography>
      ))}
    </Stack>
  );
}

/** What a text is for, above its field, so rewording it never hides its job. */
export function ControlNote({ control }: { control: Control }) {
  return (
    <Stack spacing={0.25}>
      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
        {control.name}
        {control.goesTo !== undefined && ` → ${control.goesTo}`}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {control.purpose}
        {control.shownWhen !== undefined && ` Shows: ${control.shownWhen}`}
      </Typography>
      {Object.entries(control.values ?? {}).map(([name, meaning]) => (
        <Typography key={name} variant="caption" color="text.secondary">
          {`{{${name}}}`}: {meaning} For example: {sampleOf(name)}.
        </Typography>
      ))}
    </Stack>
  );
}

/** Your learning goal's pick limit, and the rules a learner meets at it. */
export function GoalLimit({
  step,
  disabled,
  onStep,
}: {
  step: BuiltIn;
  disabled: boolean;
  onStep: (next: FlowStep) => void;
}) {
  const limit = step.limit ?? GOALS_MAX;
  return (
    <Stack spacing={1.5}>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
        <Typography variant="body2" id="goal-limit">
          Most goals a learner can pick
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={limit}
          disabled={disabled}
          aria-labelledby="goal-limit"
          onChange={(_, next: (typeof LIMITS)[number] | null) => {
            if (next !== null) onStep({ ...step, limit: next });
          }}
        >
          {LIMITS.map((one) => (
            <ToggleButton key={one} value={one}>
              {one}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>
      <Rules
        items={[
          limit === 1
            ? 'Learners pick one goal.'
            : `Learners pick up to ${String(limit)} goals.`,
          'Continue works once at least one goal is picked.',
          'At the limit, the other goals look grayed out until the learner unpicks one.',
          'A learner who picked more before the limit was lowered keeps those picks, but Continue stays off until they unpick down to the limit.',
          '“Something else” counts as a pick. Continue stays off until the learner writes their goal.',
          '“I’m just exploring” clears any picks and goes to the next screen.',
          'The picked goals choose which topics are suggested on the next screens.',
          'Try each rule in the preview.',
        ]}
      />
    </Stack>
  );
}

/**
 * Your starting topic's rules, read from `onboarding-flow.ts` (when it shows), the
 * player's Focus step and `V2OnboardingFocus` (which actions show in which state).
 */
export function FocusNotes({ words }: { words: (key: string) => string }) {
  const quote = (key: string) => `“${words(key)}”`;
  return (
    <Stack spacing={1.5}>
      <Typography variant="subtitle2">What it shows</Typography>
      <Rules
        items={[
          'The topics the learner picked on Choose your interests. Topics that are not ready show as Coming soon and cannot be picked.',
          `One ready topic is labelled ${quote('focus.suggested')} and listed first. It is the one the most picked goals suggest; a tie goes to the goal picked first. “Something else” and “I’m just exploring” never count. With no goal behind any ready topic, the first one in the app’s topic order is listed first, without the label.`,
        ]}
      />
      <Typography variant="subtitle2">Who sees it</Typography>
      <Rules
        items={[
          'Only learners who picked 2 or more interests, at least one of them ready. Everyone else skips it.',
          'A topic is ready once one of its opening lessons is published (Topic starts tab).',
        ]}
      />
      <Typography variant="subtitle2">The buttons</Typography>
      <Rules
        items={[
          `${quote('focus.start')}: when at least one interest is ready. Off until the learner picks a topic, then starts with it.`,
          `${quote('focus.tryCase')}: when none of the interests is ready but another topic is. Starts with the ready topic that best fits the goals.`,
          `${quote('focus.editInterests')}: when none of the interests is ready. Goes back to Choose your interests.`,
        ]}
      />
      <Typography variant="caption" color="text.secondary">
        To see each state, use Topics ready beside the preview.
      </Typography>
    </Stack>
  );
}

/** Who the preview plays, and on Your starting topic, which of their topics are ready. */
export function PreviewLearner({
  sample,
  ready,
  onReady,
  goalName,
}: {
  sample: PreviewSample;
  /** Absent hides the control: it only changes Your starting topic. */
  ready?: TopicsReady | undefined;
  onReady: (next: TopicsReady) => void;
  goalName: (goal: OnboardingGoal) => string;
}) {
  const interests = sample.interests.map((topic) =>
    sample.unready.includes(topic)
      ? `${SUBJECT_LABEL[topic]} (not ready)`
      : SUBJECT_LABEL[topic],
  );
  return (
    <Stack spacing={1} sx={{ maxWidth: device.width }}>
      {ready !== undefined && (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Typography variant="body2" id="topics-ready">
            Topics ready
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={ready}
            aria-labelledby="topics-ready"
            onChange={(_, next: TopicsReady | null) => {
              if (next !== null) onReady(next);
            }}
          >
            {(Object.keys(TOPICS_READY) as TopicsReady[]).map((one) => (
              <ToggleButton key={one} value={one}>
                {TOPICS_READY[one]}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>
      )}
      <Typography variant="caption" color="text.secondary">
        The preview learner picked these goals:{' '}
        {sample.goals.map(goalName).join(', ')}. And these interests:{' '}
        {interests.join(', ')}.
      </Typography>
    </Stack>
  );
}
